import { useState, useEffect, useCallback } from 'react'
import Sidebar from './components/layout/Sidebar'
import ChatContainer from './components/chat/ChatContainer'
import InputBar from './components/chat/InputBar'
import SettingsPopup from './components/common/SettingsPopup'
import ChatHeader from './components/chat/ChatHeader'
import { apiService, promptService } from './services'
import { transformThreadsForSidebar, transformThreadToMessages } from './services/api/threadTransformer'
import MapTest from './components/chat/maps/MapTest'
import MapExample from './components/chat/maps/MapExample'

function App() {
  // Core state
  const [messages, setMessages] = useState([])
  const [histories, setHistories] = useState([])
  const [currentThreadId, setCurrentThreadId] = useState(() => localStorage.getItem('currentThreadId'))
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingHistories, setIsLoadingHistories] = useState(true)
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  
  // Initialize default settings if they don't exist
  useEffect(() => {
    // Set default value for useAdvancedReasoning if it doesn't exist
    if (localStorage.getItem('useAdvancedReasoning') === null) {
      localStorage.setItem('useAdvancedReasoning', 'true');
    }
    // Generate userId for anonymous users if it doesn't exist
    if (!localStorage.getItem('userId')) {
      localStorage.setItem('userId', crypto.randomUUID());
    }
    // Initialize prompts from backend
    promptService.init();
  }, []);
  
  // Keep-alive function that calls the health API endpoint every minute
  useEffect(() => {
    const keepAliveInterval = setInterval(async () => {
      try {
        await apiService.checkHealth();
      } catch (error) {
        // Health check failed silently
      }
    }, 60000); // Run every 60 seconds (1 minute)

    // Clean up the interval when the component unmounts
    return () => clearInterval(keepAliveInterval);
  }, []);

  // Load threads from API on mount
  useEffect(() => {
    const loadThreads = async () => {
      try {
        setIsLoadingHistories(true);
        const response = await apiService.getThreads();
        const threads = transformThreadsForSidebar(response.threads || []);
        setHistories(threads);
        console.log('[App] Loaded threads from API:', threads.length);
      } catch (error) {
        console.error('[App] Failed to load threads from API:', error);
        setHistories([]);
      } finally {
        setIsLoadingHistories(false);
      }
    };
    loadThreads();
  }, []);

  // Function to refresh threads from API
  const refreshThreads = useCallback(async () => {
    try {
      const response = await apiService.getThreads();
      const threads = transformThreadsForSidebar(response.threads || []);
      setHistories(threads);
      return threads;
    } catch (error) {
      console.error('[App] Failed to refresh threads:', error);
      return histories;
    }
  }, [histories]);

  // Check if we're in test mode
  const urlParams = new URLSearchParams(window.location.search);
  const testMode = urlParams.get('test');

  // PERFORMANCE FIX: Wrapped in useCallback to prevent infinite loops
  const handleNewChat = useCallback(async (threadId) => {
    console.log('[handleNewChat] Starting new chat, threadId provided:', threadId);
    try {
      // If threadId is provided, use it (Sidebar already created the thread)
      if (threadId) {
        console.log('[handleNewChat] Using provided threadId:', threadId);
        localStorage.setItem('currentThreadId', threadId);

        // Clear messages and refresh threads from API
        setMessages([]);
        setCurrentThreadId(threadId);
        await refreshThreads();
      } else {
        // No threadId provided, create a new thread
        console.log('[handleNewChat] No threadId provided, creating new thread via API');
        const threadResponse = await apiService.createThread();
        console.log('[handleNewChat] API response:', threadResponse);

        const newThreadId = threadResponse.id || threadResponse.threadId;
        console.log('[handleNewChat] New threadId from API:', newThreadId);

        if (!newThreadId) {
          throw new Error('Failed to get thread ID from API');
        }

        localStorage.setItem('currentThreadId', newThreadId);

        // Clear messages and refresh threads from API
        console.log('[handleNewChat] Clearing messages for new thread');
        setMessages([]);
        setCurrentThreadId(newThreadId);
        await refreshThreads();
      }
    } catch (error) {
      console.error('[handleNewChat] Failed to create new chat:', error);
      throw error; // Re-throw so callers know it failed
    }
  }, [refreshThreads]); // Depends on refreshThreads

  // Initialize chat only once on mount
  useEffect(() => {
    const initializeChat = async () => {
      // Always create a new thread on page load/refresh
      try {
        const threadResponse = await apiService.createThread();
        const newThreadId = threadResponse.id || threadResponse.threadId;
        if (newThreadId) {
          localStorage.setItem('currentThreadId', newThreadId);
          setMessages([]);
          setCurrentThreadId(newThreadId);
        }
      } catch (error) {
        console.error('[initializeChat] Failed to create initial thread:', error);
      }
    };

    initializeChat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty array - truly only run once on mount

  // Messages are now stored server-side automatically via POST /threads/{threadId}
  // This effect refreshes the thread list when a new bot message is received
  useEffect(() => {
    if (currentThreadId && messages.length > 0) {
      const lastMessage = messages[messages.length - 1]
      if (lastMessage.type === 'bot' && !lastMessage.isHistoryUpdated) {
        // Mark message as processed
        lastMessage.isHistoryUpdated = true
        // Refresh threads from API to update sidebar (title may have changed)
        refreshThreads()
      }
    }
  }, [messages, currentThreadId, refreshThreads])

  // Add event listener for removeMessage event
  useEffect(() => {
    const handleRemoveMessage = (e) => {
      const { index } = e.detail;
      setMessages(prev => {
        const newMessages = [...prev];
        if (index === -1) {
          // Special case: remove the last message
          newMessages.pop();
        } else if (index >= 0 && index < newMessages.length) {
          newMessages.splice(index, 1);
        }
        return newMessages;
      });
    };

    window.addEventListener('removeMessage', handleRemoveMessage);
    return () => {
      window.removeEventListener('removeMessage', handleRemoveMessage);
    };
  }, []);

  // Add event listener for updateMessage event
  useEffect(() => {
    const handleUpdateMessage = (e) => {
      const { index, updates } = e.detail;
      setMessages(prev => {
        const newMessages = [...prev];
        if (index === -1 && newMessages.length > 0) {
          // Special case: update the last message
          const lastIndex = newMessages.length - 1;
          newMessages[lastIndex] = { ...newMessages[lastIndex], ...updates };
        } else if (index >= 0 && index < newMessages.length) {
          // Update the specified message
          newMessages[index] = { ...newMessages[index], ...updates };
        }
        return newMessages;
      });
    };

    window.addEventListener('updateMessage', handleUpdateMessage);
    return () => {
      window.removeEventListener('updateMessage', handleUpdateMessage);
    };
  }, []);

  // Handle filling the input box with a suggestion (without submitting)
  const handleFillInput = useCallback((questionText) => {
    // Dispatch event to set the input value
    window.dispatchEvent(new CustomEvent('setInputValue', {
      detail: { value: questionText }
    }));

    // Focus the input after a short delay
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('focusChatInput'));
    }, 100);
  }, []);

  const handleSendMessage = async (message) => {
    // If it's a question, set loading state
    if (message.type === 'question') {
      setIsLoading(true);
    }

    // Add message to state
    const messageWithFlag = message.type === 'bot'
      ? { ...message, isHistoryUpdated: false }
      : message;

    setMessages(prev => [...prev, messageWithFlag]);

    // If it's a bot response, clear loading state
    if (message.type === 'bot') {
      setIsLoading(false);
    }
  };

  const clearAndLoadChat = async (threadId) => {
    console.log('[clearAndLoadChat] Loading chat for threadId:', threadId);
    // Set loading state
    setIsLoading(true)

    // Clear current messages immediately
    setMessages([])

    try {
      // Get thread from API
      const apiThread = await apiService.getThread(threadId)
      console.log('[clearAndLoadChat] Loaded thread from API:', apiThread ? 'yes' : 'no', apiThread?.messages?.length, 'messages');

      if (apiThread) {
        // Set the current thread ID
        setCurrentThreadId(threadId)
        localStorage.setItem('currentThreadId', threadId)

        // Transform API messages into the format expected by ChatContainer
        const formattedMessages = transformThreadToMessages(apiThread)

        // Use setTimeout to ensure messages are cleared first
        setTimeout(() => {
          setMessages(formattedMessages)
          setIsLoading(false)
        }, 0)
      } else {
        setIsLoading(false)
      }
    } catch (error) {
      console.error('[clearAndLoadChat] Failed to load thread:', error)
      setIsLoading(false)
    }
  }

  const handleSelectChat = (threadId) => {
    console.log('[handleSelectChat] User selected chat:', threadId);
    clearAndLoadChat(threadId)
  }

  const handleClearHistory = async (remainingHistories = []) => {
    // Get the threads to delete (ones not in remainingHistories)
    const threadsToDelete = histories.filter(
      h => !remainingHistories.find(r => r.threadId === h.threadId)
    );

    // Delete each thread via API
    for (const thread of threadsToDelete) {
      try {
        await apiService.deleteThread(thread.threadId);
        console.log('[handleClearHistory] Deleted thread:', thread.threadId);
      } catch (error) {
        console.error('[handleClearHistory] Failed to delete thread:', thread.threadId, error);
      }
    }

    // Update state with remaining histories
    setHistories(remainingHistories);
    setMessages([]);

    // Clear current thread if it was deleted
    const currentThread = remainingHistories.find(h => h.threadId === currentThreadId);
    if (!currentThread) {
      setCurrentThreadId(null);
      localStorage.removeItem('currentThreadId');
    }
  };

  // If we're in test mode, render the test component
  if (testMode === 'map') {
    return (
      <div className="h-screen bg-gray-50">
        <MapTest />
      </div>
    );
  }
  
  if (testMode === 'mapexample') {
    return (
      <div className="h-screen bg-gray-50">
        <MapExample />
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 text-gray-900">
      <div className="h-full flex flex-col">
        <main className="flex-1 flex overflow-hidden">
          <Sidebar 
            chats={histories}
            onNewChat={handleNewChat}
            onSelectChat={handleSelectChat}
            onUpdateChats={setHistories}
            onToggleSettings={() => {
              // Clear messages and create a new thread when settings is toggled
              setMessages([]);
              handleNewChat();
              // We don't actually need to open settings here
            }}
          />
          <div className="flex-1 flex flex-col">
            <ChatHeader 
              setIsSettingsOpen={setIsSettingsOpen} 
              onClearHistory={handleClearHistory}
            />
            <ChatContainer
              messages={messages}
              isLoading={isLoading}
              onSuggestionClick={handleFillInput}
            />
            <InputBar onSendMessage={handleSendMessage} onNewChat={handleNewChat} />
          </div>
        </main>
        <SettingsPopup
          isOpen={isSettingsOpen}
          onClose={() => setIsSettingsOpen(false)}
          onClearHistory={handleClearHistory}
          histories={histories}
        />
      </div>
    </div>
  )
}

export default App 